import React, { useState, useCallback } from 'react';

const ContributionItem = React.memo(({
  contributionData,
  prefix,
  isMainPart,
  isExpanded,
  onToggleExpand,
  onShowPodium,
}) => {
  const [copyFeedback, setCopyFeedback] = useState(false);

  const bestContribution = contributionData?.contributions?.[0];
  const texte = bestContribution?.contribution || ''; 

  const maxDisplayLength = 200;
  const isLong = texte.length > maxDisplayLength;
  
  const displayedText = isExpanded ? texte : (isLong ? texte.slice(0, maxDisplayLength) + "..." : texte);

  const handleContainerClick = useCallback(() => {
    if (isLong) {
      onToggleExpand();
    }
  }, [isLong, onToggleExpand]);

  const handlePodiumClick = useCallback((e) => {
    e.stopPropagation();
    onShowPodium(contributionData.contributions); 
  }, [contributionData.contributions, onShowPodium]);

  const handleCopyClick = useCallback(async (e) => {
    e.stopPropagation();
    
    if (copyFeedback) return;

    if (!texte || texte.trim() === '') {
      console.error('❌ Aucun texte à copier');
      return;
    }

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(texte);
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = texte;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (successful) {
            setCopyFeedback(true);
            setTimeout(() => setCopyFeedback(false), 2000);
          } else {
            throw new Error('execCommand a échoué');
          }
        } catch (fallbackErr) {
          document.body.removeChild(textArea);
          throw fallbackErr;
        }
      }
    } catch (error) {
      console.error('❌ Erreur lors de la copie :', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback(false), 2000);
    }
  }, [copyFeedback, texte]);

  return (
    <div style={{ marginLeft: isMainPart ? '0' : '1.5rem' }}>
      <div
        className={`p-4 rounded-lg border-2 transition-all duration-200 flex flex-col gap-3 bloc-contribution ${
          isExpanded
            ? 'border-primary bg-primary-light'
            : 'border-border-color bg-white hover:border-primary hover:shadow-lg'
        } ${isMainPart ? 'border-l-4 border-l-primary font-semibold' : ''} ${isLong ? 'cursor-pointer' : ''}`}
        onClick={handleContainerClick}
      >
        <div className="flex items-start gap-3 groupe-contribution">
          <span className="text-primary font-bold text-lg flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
            {prefix}.
          </span>
          <div className="flex-1 min-w-0">
            <span className={`${isExpanded ? 'font-medium' : ''} text-gray-700 block contribution-text-item`}>
              {displayedText}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center mt-auto pt-2 border-t border-border-color">
          <div className="flex-1 min-w-0">
            {isLong && !isExpanded && (
              <span className="text-xs text-secondary" style={{ fontWeight: 'normal' }}>
                Cliquer pour voir le texte complet...
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleCopyClick}
              className={`btn-action-compact ${
                copyFeedback === true ? 'btn-action-success' : 
                copyFeedback === 'error' ? 'btn-action-error' : ''
              }`}
              title="Copier le texte"
              style={{width:'auto',minWidth:'32px'}}
              disabled={!texte || copyFeedback === true}
            >
              {copyFeedback === true ? '✅' : 
               copyFeedback === 'error' ? '❌' : '📋'}
              
              <span className="hidden md:inline ml-2" style={{paddingRight:'0.5rem'}}>
                {copyFeedback === true ? 'Copié !' : 
                 copyFeedback === 'error' ? 'Erreur' : 'Copier'}
              </span>
            </button>
            
            <button
              onClick={handlePodiumClick}
              className="btn-action-compact btn-action-primary"
              title="Voir le podium"
              style={{width:'auto',minWidth:'32px'}}
            >
              🏆
              <span className="hidden md:inline ml-2" style={{paddingRight:'0.5rem'}}>
                Podium
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.prefix === nextProps.prefix &&
         prevProps.isMainPart === nextProps.isMainPart &&
         prevProps.isExpanded === nextProps.isExpanded &&
         JSON.stringify(prevProps.contributionData) === JSON.stringify(nextProps.contributionData);
});

ContributionItem.displayName = 'ContributionItem';

export default ContributionItem;